/**
 * Handing the browser a file.
 *
 * Everything the app exports is computed in the browser from the local store —
 * there is no export endpoint, because there is nothing a server could add
 * except latency and a second copy of the data. That makes downloading an
 * object-URL-and-a-click, which is small enough that it lives here rather than
 * being repeated in three components.
 */

/**
 * Trigger a download of `blob` as `filename`.
 *
 * The object-URL-and-a-click itself, with nothing said about what is in the
 * file: text exports go through {@link downloadText}, and a QR code is a PNG
 * the canvas already encoded, so it arrives here as bytes.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next frame rather than immediately: Safari cancels a
  // download whose blob URL is revoked in the same task as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Trigger a download of `text` as `filename`. */
export function downloadText(filename: string, text: string, mimeType: string): void {
  downloadBlob(filename, new Blob([text], { type: `${mimeType};charset=utf-8` }));
}

/** Trigger a download of a JSON value, pretty-printed. */
export function downloadJson(filename: string, value: unknown): void {
  downloadText(filename, `${JSON.stringify(value, null, 2)}\n`, 'application/json');
}

/**
 * A filename-safe version of a document's name: `Brewer's Best Pale Ale` →
 * `brewers-best-pale-ale`.
 *
 * `fallback` is what an unnamed document is called. It defaults to `recipe`,
 * which is what every caller but the journal's QR download wants; a journal
 * that has never been named should not produce `recipe-qr.png`.
 */
export function slugify(name: string, fallback = 'recipe'): string {
  const slug = name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    // Apostrophes are removed rather than replaced, so a possessive stays one
    // word: `brewers`, not `brewer-s`.
    .replace(/['\u2019]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return slug.length === 0 ? fallback : slug;
}

/**
 * Read an uploaded file as **bytes**.
 *
 * Bytes, not text: `File.text()` always decodes as UTF-8, and BeerSmith writes
 * ISO-8859-1 by default — reading its exports as text turns every accented
 * maltster into a replacement character before the parser ever sees the
 * declaration that would have decoded them (see the interchange package's
 * `decodeXmlBytes`).
 *
 * `Blob.arrayBuffer` is the modern path; the `FileReader` fallback covers the
 * runtimes that lack it, which today means older Safari and jsdom.
 */
export async function readFileBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsArrayBuffer(file);
  });
}
